import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  message,
  Select,
  Row,
  Col,
  Spin,
} from "antd";
import ProtocolServices from "../../services/ProtocolServices";
import CompanyServices from "../../services/CompanyServices";
import SimpleReactValidator from "simple-react-validator";
import { LoadingOutlined } from "@ant-design/icons";
import { translate } from "../../locale";
import PropTypes from "prop-types";

const { TextArea } = Input;
const { Option } = Select;

const AddProtocolCondition = ({
  reset,
  protocolDetails,
  visible,
  onComplete,
  onCancel,
  enterprises,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingPills, setLoadingPills] = useState(false);
  const [loadingProtocols, setLoadingProtocols] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [protocolDays, setProtocolDays] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pills, setPills] = useState([]);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    condition: "",
    type: "",
    jump: "",
    day_jump: "",
    pill: "",
  });

  const types = [
    { id: "jump", name: translate("Saltar") },
    { id: "pill", name: translate("Píldora") },
  ];
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (error) => errorMessage(error),
      messages: {
        required: "Campo requerido",
      },
    })
  );

  const errorMessage = (message) => (
    <div className="text-red-600 pt-2 text-xs">{message}</div>
  );

  const disabled = () => !simpleValidator.current.allValid();

  useEffect(() => {
    if (visible !== null) {
      setActive(visible);
      setLoading(false);
    }
    if (protocolDetails) {
      setValues((v) => ({
        ...v,
        condition: protocolDetails.type === "pill" ? "True" : "",
      }));
    }
  }, [visible, protocolDetails]);

  const loadSubscriptions = async (id_enterprise) => {
    setLoading(true);
    setLoadingSubscriptions(true);
    setValues((v) => ({
      ...v,
      id_subscription: "",
      jump: "",
      day_jump: "",
    }));
    try {
      const res = await CompanyServices.getSubscriptions(id_enterprise);
      if (
        res.status >= 200 &&
        res.status < 300 &&
        res.data.subscriptions.length > 0
      ) {
        setSubscriptions(res.data.subscriptions);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setLoadingSubscriptions(false);
    }
  };

  const loadProtocols = async (id_subscription) => {
    setLoading(true);
    setLoadingProtocols(true);
    setValues((v) => ({
      ...v,
      jump: "",
      day_jump: "",
    }));
    try {
      const res = await ProtocolServices.getProtocolsSubscription(
        id_subscription
      );
      if (
        res.status >= 200 &&
        res.status < 300 &&
        res.data.protocols.length > 0
      ) {
        setProtocols(res.data.protocols);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setLoadingProtocols(false);
    }
  };

  const loadPills = async () => {
    setLoading(true);
    setLoadingPills(true);
    try {
      const res = await ProtocolServices.getPill({ page_size: 10000 });
      if (res.status >= 200 && res.status < 300 && res.data.pills.length > 0) {
        setPills(res.data.pills);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setLoadingPills(false);
    }
  };

  const loadProtocolDetails = async (id) => {
    setLoading(true);
    try {
      const res = await ProtocolServices.getDays(id);
      if (res.status >= 200 && res.status < 300 && res.data.days.length > 0) {
        setProtocolDays(res.data.days);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("Todos los datos son requeridos");
      return;
    }

    try {
      const data = {
        protocol_id: protocolDetails.protocol,
        protocol_detail_id: protocolDetails.id,
        protocol_condition: {
          type: values.type,
          condition: values.condition,
          ...(values.type === "jump"
            ? { jump: values.jump, day_jump: values.day_jump }
            : null),
          ...(values.type === "pill" ? { pill: values.pill } : null),
          ...(values.id_subscription
            ? { jump_subs: values.id_subscription }
            : null),
        },
      };

      const res = await ProtocolServices.saveProtocolCondition(data);

      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success("El protocolo se guardó exitosamente.");
          resetForm();
          if (onComplete) {
            onComplete();
          }
        } else {
          message.error(res.data.message, 3);
        }
        setLoading(false);
      } else {
        setError("Log in error");
      }
    } catch (e) {
      console.log(e);
      setError(e.message);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };

  const resetForm = () => {
    setValues({
      condition: "",
      jump: "",
      day_jump: "",
      jump_subs: "",
      type: "",
      pill: "",
      id_subscription: "",
      id_enterprise: "",
    });
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (name === "id_enterprise") loadSubscriptions(value);
    if (name === "id_subscription") loadProtocols(value);
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };
  const handleChangeProtocol = (name, value) => {
    loadProtocolDetails(value);
    setValues((v) => ({ ...v, jump: value }));
  };

  const handleChangeType = (value) => {
    if (value === "pill") {
      loadPills();
    }
    setValues((v) => ({
      ...v,
      type: value,
      jump: "",
      day_jump: "",
      pill: "",
    }));
    simpleValidator.current.purgeFields();
    simpleValidator.current.hideMessageFor("condition");
  };
  return (
    <Modal
      open={active}
      title={`Agregar condición (Día ${
        protocolDetails !== null && protocolDetails.day
      } / Tipo ${protocolDetails !== null && protocolDetails.type_name})`}
      onOk={null}
      onCancel={handleCancel}
      footer={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
        className="w-full"
      >
        <Form.Item className={protocolDetails?.type === "pill" ? "hidden" : ""}>
          <TextArea
            placeholder="Condición"
            name="condition"
            rows={4}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.condition}
          />
          {simpleValidator.current.message(
            "condition",
            values.condition,
            "required"
          )}
        </Form.Item>

        <Form.Item>
          <Select
            name="type"
            placeholder="Seleccione tipo"
            onChange={handleChangeType}
            value={values.type}
          >
            <Option value="">Seleccione Tipo</Option>
            {types.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
              </Option>
            ))}
          </Select>
          {simpleValidator.current.message("type", values.type, "required")}
        </Form.Item>
        {values.type === "jump" && (
          <>
            <Form.Item>
              <Select
                name="id_enterprise"
                placeholder="Seleccione empresa"
                onChange={(v) =>
                  handleChange({ target: { name: "id_enterprise", value: v } })
                }
                value={values.id_enterprise}
              >
                <Option value="">Seleccione empresa</Option>
                {enterprises?.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select>
              {simpleValidator.current.message(
                "id_enterprise",
                values.id_enterprise,
                "required"
              )}
            </Form.Item>
            <Form.Item>
              <Select
                name="id_subscription"
                placeholder="Seleccione suscripción"
                onChange={(v) =>
                  handleChange({
                    target: { name: "id_subscription", value: v },
                  })
                }
                value={values.id_subscription}
                loading={loadingSubscriptions}
                disabled={!values.id_enterprise}
              >
                <Option value="">Seleccione suscripción</Option>
                {subscriptions?.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select>
              {simpleValidator.current.message(
                "id_subscription",
                values.id_subscription,
                "required"
              )}
            </Form.Item>
            <Form.Item>
              <Select
                name="jump"
                placeholder="Seleccione protocolo"
                onChange={(v) => handleChangeProtocol(`jump`, v)}
                value={values.jump}
                loading={loadingProtocols}
                disabled={!values.id_subscription}
              >
                <Option value="">Seleccione protocolo</Option>
                {protocols.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select>
              {simpleValidator.current.message("jump", values.jump, "required")}
            </Form.Item>
            <Form.Item>
              <Select
                name="day_jump"
                placeholder="Día del protocolo"
                onChange={(v) => {
                  handleChange({ target: { name: `day_jump`, value: v } });
                }}
                value={values.day_jump}
                disabled={protocolDays.length === 0}
                className="mb-0"
              >
                <Option value="">Día del protocolo</Option>
                {protocolDays.map((day) => (
                  <Option key={day} value={day}>
                    Día {day}
                  </Option>
                ))}
              </Select>
              {simpleValidator.current.message(
                "day_jump",
                values.day_jump,
                "required"
              )}
            </Form.Item>
          </>
        )}
        {values.type === "pill" && (
          <Form.Item>
            <Select
              name="pill"
              placeholder="Seleccione píldora"
              onChange={(v) =>
                handleChange({ target: { name: `pill`, value: v } })
              }
              value={values.pill}
              loading={loadingPills}
            >
              <Option value="">Seleccione píldora</Option>
              {pills.map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.name}
                </Option>
              ))}
            </Select>
            {simpleValidator.current.message("pill", values.pill, "required")}
          </Form.Item>
        )}

        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button
                disabled={disabled() || loading}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
              >
                {loading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                  />
                ) : (
                  "Enviar"
                )}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

AddProtocolCondition.propTypes = {
  reset: PropTypes.bool,
  protocolDetails: PropTypes.object,
  visible: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  enterprises: PropTypes.array,
};

export default AddProtocolCondition;
